import { Dispatch, FC, SetStateAction, useCallback } from "react";
import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@material-ui/core";
import { orderBy } from "lodash";
import moment from "moment";

import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { BuyerDashboardItem } from "containers/BuyerDashboard/assetAdministrationsPostProcessor";
import { formatCurrencyAmount, formatPercentage } from "lib/format";
import theme from "theme";

import AssetsTableRow from "./AssetsTableRow";
import HeaderCell from "./HeaderCell";

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      tableLayout: "fixed",
      "& .MuiTableCell-sizeSmall:last-child": {
        // for the "ChevronRight" header and data cell
        padding: 0,
      },
    },
  }),
);

export type Column = {
  title: string;
  getFormattedData: (item: BuyerDashboardItem) => string;
  getSortData: (item: BuyerDashboardItem) => string | number | null;
  width: string;
};
const titleColumn: Column = {
  title: "Title",
  getFormattedData: (item: BuyerDashboardItem) =>
    `${item.dealTitle ? `${item.dealTitle} ` : ""}${
      item.previousHubspotDealId ? `(Deal #${item.previousHubspotDealId})` : ""
    }`,
  getSortData: (item: BuyerDashboardItem) => item.dealTitle,
  width: "",
};

const makeColumns: (
  isResponsiveColumns: boolean,
) => Column[] = isResponsiveColumns => {
  const dateColumnWidth = isResponsiveColumns ? "13%" : "12%";
  const amountColumnWidth = isResponsiveColumns ? "15%" : "13%";
  const percentColumnWidth = isResponsiveColumns ? "7%" : "6%";
  const columns = [
    {
      title: "Purchase Date",
      getFormattedData: (item: BuyerDashboardItem) =>
        moment(item.purchasedDate).format("MM/DD/YY"),
      getSortData: (item: BuyerDashboardItem) => item.purchasedDate,
      width: dateColumnWidth,
    },
    {
      title: "Purchase Price",
      getFormattedData: (item: BuyerDashboardItem) =>
        formatCurrencyAmount(item.currency, item.purchasedPrice),
      getSortData: (item: BuyerDashboardItem) => Number(item.purchasedPrice),
      width: amountColumnWidth,
    },
    {
      title: "Royalties Received",
      getFormattedData: (item: BuyerDashboardItem) =>
        formatCurrencyAmount(item.currency, item.royaltiesReceived),
      getSortData: (item: BuyerDashboardItem) => Number(item.royaltiesReceived),
      width: amountColumnWidth,
    },
    {
      title: "Yield",
      getFormattedData: (item: BuyerDashboardItem) =>
        !!item.totalYield
          ? formatPercentage(parseFloat(item.totalYield))
          : EMPTY_VALUE_PLACEHOLDER,
      getSortData: (item: BuyerDashboardItem) =>
        !!item.totalYield ? Number(item.totalYield) : null,
      width: percentColumnWidth,
    },
    {
      title: "Sale Date",
      getFormattedData: (item: BuyerDashboardItem) =>
        item.saleDate
          ? moment(item.saleDate).format("MM/DD/YY")
          : EMPTY_VALUE_PLACEHOLDER,
      getSortData: (item: BuyerDashboardItem) => item.saleDate,
      width: dateColumnWidth,
    },
    {
      title: "Sale Proceeds",
      getFormattedData: (item: BuyerDashboardItem) =>
        item.saleProceeds
          ? formatCurrencyAmount(item.currency, item.saleProceeds)
          : EMPTY_VALUE_PLACEHOLDER,
      getSortData: (item: BuyerDashboardItem) =>
        !!item.saleProceeds ? Number(item.saleProceeds) : null,
      width: amountColumnWidth,
    },
    {
      title: "ROI\u00a0$",
      getFormattedData: (item: BuyerDashboardItem) =>
        formatCurrencyAmount(item.currency, item.returnOnInvestmentAmount),
      getSortData: (item: BuyerDashboardItem) =>
        !!item.returnOnInvestmentAmount
          ? Number(item.returnOnInvestmentAmount)
          : null,
      width: amountColumnWidth,
    },
    {
      title: "ROI\u00a0%",
      getFormattedData: (item: BuyerDashboardItem) =>
        item.returnOnInvestmentPercentage
          ? formatPercentage(parseFloat(item.returnOnInvestmentPercentage))
          : EMPTY_VALUE_PLACEHOLDER,
      getSortData: (item: BuyerDashboardItem) =>
        !!item.returnOnInvestmentPercentage
          ? Number(item.returnOnInvestmentPercentage)
          : null,
      width: percentColumnWidth,
    },
    {
      title: "Last Updated",
      getFormattedData: (item: BuyerDashboardItem) =>
        moment(item.lastUpdate).format("MM/DD/YY"),
      getSortData: (item: BuyerDashboardItem) => item.lastUpdate,
      width: dateColumnWidth,
    },
  ];

  return isResponsiveColumns ? columns.slice(0, -1) : columns;
};

export const defaultSort = {
  column: makeColumns(false)[0],
  isSortingAsc: true,
};

interface AssetsTableProps {
  buyerDashboardItems: BuyerDashboardItem[];
  sortColumn: Column;
  setSortColumn: Dispatch<SetStateAction<Column>>;
  isSortingAsc: boolean;
  setIsSortingAsc: Dispatch<SetStateAction<boolean>>;
  onDetailsClick: (buyerDashboardItem: BuyerDashboardItem) => void;
}

const AssetsTable: FC<AssetsTableProps> = ({
  buyerDashboardItems,
  sortColumn,
  setSortColumn,
  isSortingAsc,
  setIsSortingAsc,
  onDetailsClick,
}) => {
  const classes = useStyles();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const responsiveColumns = makeColumns(isMdDown);

  const handleHeaderCellClick = useCallback(
    (column: Column) => {
      setSortColumn(column);
      setIsSortingAsc(prev => {
        if (sortColumn.title === column.title) {
          return !prev;
        }
        return true;
      });
    },
    [setIsSortingAsc, setSortColumn, sortColumn],
  );

  const sortedBuyerDashboardItems = orderBy(
    buyerDashboardItems,
    // NOTE: first elements of the sort-definition arrays implements "nulls last"
    [asset => sortColumn.getSortData(asset) === null, sortColumn.getSortData],
    ["asc", isSortingAsc ? "asc" : "desc"],
  );

  return (
    <Table size="small" className={classes.table}>
      <TableHead>
        <TableRow>
          {responsiveColumns.map(column => {
            return (
              <HeaderCell
                key={column.title}
                isSorting={sortColumn.title === column.title}
                isSortingAsc={isSortingAsc}
                title={column.title}
                onClick={() => handleHeaderCellClick(column)}
                width={column.width}
              />
            );
          })}
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedBuyerDashboardItems.map((buyerDashboardItem, i) => (
          <AssetsTableRow
            key={i}
            buyerDashboardItem={buyerDashboardItem}
            responsiveColumns={responsiveColumns}
            titleColumn={titleColumn}
            isLastRow={i === sortedBuyerDashboardItems.length - 1}
            onDetailsClick={onDetailsClick}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default AssetsTable;
